import { QueryObserver } from "@tanstack/react-query"
import { isEqual } from "lodash-es"
import { computed, type IComputedValue, type IObservableValue, observable, reaction, runInAction } from "mobx"

import { batchInfo, emptyBatch } from "@/lib/apiClient/client.ts"
import { withGlobalLoader } from "@/lib/context.ts"
import { formatDate } from "@/lib/date.ts"
import { formatTaskTitle } from "@/lib/displayUtils.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { projectQueryKey } from "@/lib/queries.ts"
import { TaskListId } from "@/routes/tasks/TaskListScene/TaskListId.ts"
import { tasksByRepoKey } from "@/routes/tasks/tasksHooks.ts"

import type {
  AiAgentTask,
  Batch,
  GitHubPrInfo,
  PR_Project2,
  ProjectKey,
  RepositoryFetchStatus
} from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"

const log = getLogger("TaskListModel")

export class TaskListModel extends Model {
  readonly tasks: IComputedValue<AiAgentTask[]>
  readonly tasksByDate: IObservableValue<{ date: string, tasks: AiAgentTask[] }[]>

  readonly error = observable.box<Error | null>(null)

  readonly project = observable.box<PR_Project2 | null>(null)

  readonly page: number
  readonly filter: TaskListId
  readonly successful: boolean

  readonly favoriteProjects = observable.box<ProjectKey[]>([])
  readonly isFavoriteProject: IComputedValue<boolean>

  readonly hasMore: IComputedValue<boolean>
  readonly hasPrev: IComputedValue<boolean>
  readonly hasNext: IComputedValue<boolean>

  readonly gitHubPrs = observable.box<Map<string, GitHubPrInfo[]>>(new Map())

  private readonly hasFetchedData = observable.box(false)
  private readonly isFetching = observable.box(false)

  private readonly batch = observable.box<Batch<AiAgentTask>>(emptyBatch())
  private readonly tasksPromise: Promise<void>

  private readonly tasksDeleted = observable.box<string[]>([])

  projectRepository = computed(() => this.project.get()?.repository.name)
  readonly repoSyncStatus = observable.box<RepositoryFetchStatus | null>(null)

  constructor(private readonly context: OrgContext, params: TaskListModelParams) {
    super("TaskListModel")

    runInAction(() => this.project.set(params.project))

    this.page = params.page
    this.filter = params.type ?? TaskListId.All
    this.successful = params.successful

    this.tasks = computed(() => this.batch.get().data)
    this.tasksByDate = computed(this.listTasksByDate)
    this.tasksPromise = this.refreshTasks()

    this.isFavoriteProject = computed(() => {
      const favorites = this.favoriteProjects.get()
      return favorites.some(it => {
        return it.key === this.project.get()?.key.key
      })
    })

    this.hasMore = computed(() => {
      return this.hasPrev.get() || this.hasNext.get()
    })

    this.hasPrev = computed(() => {
      return this.page > 0
    })

    this.hasNext = computed(() => {
      const batch = this.batch.get()
      return batch.next != null && batch.next !== batch.totalCount?.toString()
    })

    this.lifetime.add(reaction(
      () => ({
        isFetching: this.isFetching.get(),
        hasFetchedData: this.hasFetchedData.get(),
      }),
      ({ isFetching, hasFetchedData }) => {
        this.isLoading.set(isFetching && !hasFetchedData)
      }
    ), "TaskListModel:isLoading")

    // Keep GitHub PRs in sync with the current tasks list
    this.lifetime.add(reaction(
      () => this.tasks.get(),
      async () => {
        try {
          const tasks = this.tasks.get()
          const prsMap = await this.subscribeToGitHubPrStatus(tasks)
          runInAction(() => this.gitHubPrs.set(prsMap))
        } catch (_) {
          // ignore errors, leave previous state
        }
      },
      { fireImmediately: true }
    ), "TaskListModel:gitHubPrs")

    const projectKey = this.project.get()?.key

    if (projectKey) {
      // Refetch on project invalidation (e.g., when the project is renamed)
      const observer = new QueryObserver(this.context.queryClient, {
        ...projectQueryKey(projectKey),
        queryFn: () => context.queries.getProjectV2(projectKey)
      })

      const unsubscribe = observer.subscribe((event) => {
        if (event.isStale && !event.isFetching && !event.isRefetching) {
          void context.queries.getProjectV2(projectKey).then((project) => {
            runInAction(() => {
              if (!isEqual(project, this.project.get())) {
                this.project.set(project)
              }
            })
          })
        }
      })

      this.lifetime.add(() => {
        unsubscribe()
      }, "TaskListModel:unsubscribe")

      void this.subscribeToRepoSyncStatus(projectKey)
    }
  }

  async awaitReady() {
    try {
      await this.tasksPromise
    } catch {
      // ignore
    }
    return true
  }

  readonly deleteTask = async (taskId: string) => {
    runInAction(() => this.tasksDeleted.set(this.tasksDeleted.get().concat(taskId)))

    await withGlobalLoader(this.context, this.context.queries.apiClient.deleteTask(taskId))

    const projectKey = this.project.get()?.key
    if (projectKey) {
      void this.context.queryClient.invalidateQueries(tasksByRepoKey(projectKey))
    }
  }

  private async refreshTasks() {
    const batchSize = 50
    const nextBatch = batchInfo(batchSize, (batchSize * this.page).toString())

    runInAction(() => this.isFetching.set(true))

    let principalId: string | null = null
    let coopOnly: boolean | null = null

    if (this.filter === TaskListId.My) {
      const principal = await this.context.queries.me()
      principalId = principal?.id as string
    } else if (this.filter === TaskListId.Coop) {
      coopOnly = true
    }

    try {
      const projectKey = this.project.get()?.key
      const projectRepo = this.projectRepository.get()

      if (!projectKey || !projectRepo) return

      const [batch, favorites] = await withGlobalLoader(this.context, Promise.all([
        this.context.queries.listTasks(projectKey, projectRepo, principalId, this.successful, coopOnly, nextBatch),
        this.context.queries.favoriteProjects()
      ]))

      runInAction(() => {
        this.batch.set(batch)
        this.error.set(null)
        this.favoriteProjects.set(favorites)
        this.isFetching.set(false)
        this.hasFetchedData.set(true)
      })
    } catch (e) {
      runInAction(() => {
        this.error.set(e as Error)
        this.isFetching.set(false)
      })
    }
  }

  private async subscribeToRepoSyncStatus(projectKey: ProjectKey) {
    try {
      const repository = this.projectRepository.get()
      if (!repository) return

      const subscription = await this.context.queries.subscribeToRepositorySyncErrors(projectKey, repository)
      runInAction(() => {
        this.repoSyncStatus.set(subscription.initial)
      })

      const channel = subscription.channel
      this.lifetime.add(channel.subscribe(status => {
        runInAction(() => this.repoSyncStatus.set(status))
      }), "TaskListModel:subscribeToRepoSyncStatus")
    } catch (e) {
      log.error("Failed to subscribe to repo sync status:", e)
    }
  }

  private readonly listTasksByDate = () => {
    const groups: Record<string, AiAgentTask[]> = {}

    const tasks = this.tasks.get().map(task => Object.assign({}, task, {
      title: formatTaskTitle(task.title),
    }))

    const tasksDeleted = new Set(this.tasksDeleted.get())

    for (const task of tasks) {
      if (tasksDeleted.has(task.id)) {
        continue
      }

      const date = formatDate(task.createdAt)
      groups[date] = groups[date] || []

      groups[date].push(task)
    }

    return Object.keys(groups).map(it => ({ date: it, tasks: groups[it] }))
  }

  private async subscribeToGitHubPrStatus(tasks: AiAgentTask[]): Promise<Map<string, GitHubPrInfo[]>> {
    try {

      const taskIds = tasks.map(t => t.id)
      if (taskIds.length === 0) return new Map()

      // Tasks list is refreshed every minute, we can ignore the subscription here
      const subscription = await withGlobalLoader(this.context, this.context.queries.subscribeToOpenedPullRequests(taskIds))

      const result = new Map<string, GitHubPrInfo[]>()
      for (const pair of subscription.initial) {
        result.set(pair.first as string, pair.second.sort((a, b) => a.number - b.number))
      }

      return result
    } catch (e) {
      log.error("Failed to subscribe to GitHub PRs status:", e)
      return new Map()
    }
  }
}

type TaskListModelParams = {
  project: PR_Project2
  page: number
  type?: TaskListId
  successful: boolean
}